import { useState } from 'react'

import { Modal, Select } from '@mantine/core'

import { useMutation, useQuery } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  BULK_GRANT_PERMISSION,
  BULK_REVOKE_PERMISSION,
  GET_ALL_PERMISSIONS,
} from 'src/graphql/permissions.graphql'

interface BulkPermissionItemResult {
  employeeId: string
  success: boolean
  skipped: boolean
  errorMsg?: string
}

export interface BulkPermissionTarget {
  employeeId: string
  name: string
}

interface BulkPermissionsModalProps {
  opened: boolean
  close: () => void
  mode: 'grant' | 'revoke'
  employees: BulkPermissionTarget[]
  /** Called after a fully successful run, to clear selection and refresh the list. */
  onCompleted: () => void
  /** Called after a partial failure with the employees that failed, so they stay selected for a retry. */
  onPartialFailure: (employeeIds: string[]) => void
}

const BulkPermissionsModal = ({
  opened,
  close,
  mode,
  employees,
  onCompleted,
  onPartialFailure,
}: BulkPermissionsModalProps) => {
  const [selectedPermission, setSelectedPermission] = useState<string | null>(
    null
  )
  const [failures, setFailures] = useState<BulkPermissionItemResult[]>([])
  const isGrant = mode === 'grant'

  const { data: permissionsData, loading: permissionsLoading } =
    useQuery(GET_ALL_PERMISSIONS)
  const allPermissions = permissionsData?.permissions ?? []

  const nameFor = (employeeId: string) =>
    employees.find((employee) => employee.employeeId === employeeId)?.name ??
    employeeId

  const handleResults = (results: BulkPermissionItemResult[]) => {
    const changed = results.filter((r) => r.success && !r.skipped).length
    const skipped = results.filter((r) => r.success && r.skipped).length
    const failed = results.filter((r) => !r.success)

    const verb = isGrant ? 'Granted to' : 'Revoked from'
    const parts = [`${verb} ${changed} user${changed === 1 ? '' : 's'}`]
    if (skipped > 0) {
      parts.push(`${skipped} unchanged`)
    }
    if (failed.length > 0) {
      parts.push(`${failed.length} failed`)
    }
    toast(parts.join(', '), failed.length > 0 ? 'error' : 'success')

    // On a partial failure, stay open with the reasons and keep just the failed
    // users selected — closing would leave no way to see who failed or retry.
    if (failed.length > 0) {
      setFailures(failed)
      onPartialFailure(failed.map((r) => r.employeeId))
      return
    }

    setFailures([])
    onCompleted()
    close()
  }

  const [bulkMutation, { loading }] = useMutation(
    isGrant ? BULK_GRANT_PERMISSION : BULK_REVOKE_PERMISSION,
    {
      // The user drawer reads employeePermissions/permissionAuditLog per
      // employee with the default cache-first policy, so a bulk change would
      // otherwise leave it showing (and offering to grant) stale permissions.
      update: (cache) => {
        cache.evict({ id: 'ROOT_QUERY', fieldName: 'employeePermissions' })
        cache.evict({ id: 'ROOT_QUERY', fieldName: 'permissionAuditLog' })
        cache.gc()
      },
      onCompleted: (data) =>
        handleResults(
          isGrant
            ? data.bulkGrantPermission.results
            : data.bulkRevokePermission.results
        ),
      onError: () => {
        toast('Unable to update permissions, please try again.', 'error')
      },
    }
  )

  const handleSubmit = () => {
    bulkMutation({
      variables: {
        input: {
          employeeIds: employees.map((employee) => employee.employeeId),
          permissionKey: selectedPermission,
        },
      },
    })
  }

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      title={isGrant ? 'Grant Permission' : 'Revoke Permission'}
      classNames={{
        title: 'font-medium',
      }}
      size="40%"
    >
      <div className="flex flex-col gap-4">
        <div className="text-sm text-doubleNickel-gray-600">
          {isGrant ? 'Grant a permission to' : 'Revoke a permission from'}{' '}
          <span className="font-medium">{employees.length}</span> selected user
          {employees.length === 1 ? '' : 's'}. Users that already{' '}
          {isGrant ? 'have' : 'lack'} the permission are left unchanged.
        </div>
        <Select
          label="Permission"
          placeholder="Select permission"
          withAsterisk
          searchable
          value={selectedPermission}
          onChange={setSelectedPermission}
          disabled={permissionsLoading}
          data={allPermissions.map((permission) => ({
            value: permission.key,
            label: permission.description || permission.key,
          }))}
        />

        {failures.length > 0 && (
          <div className="flex flex-col gap-2 rounded-md border border-doubleNickel-error-200 bg-doubleNickel-error-25 p-3">
            <div className="text-xs font-semibold text-doubleNickel-error-700">
              Could not update {failures.length} user
              {failures.length === 1 ? '' : 's'}
            </div>
            {failures.map((failure) => (
              <div
                key={failure.employeeId}
                className="text-xs text-doubleNickel-gray-700"
              >
                <span className="font-medium">
                  {nameFor(failure.employeeId)}
                </span>
                {failure.errorMsg ? ` — ${failure.errorMsg}` : ''}
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-row gap-3 border-t pt-4">
          <Button
            text={failures.length > 0 ? 'Done' : 'Cancel'}
            variant="outline"
            className="flex-1"
            onClick={close}
          />
          <Button
            text={failures.length > 0 ? 'Retry' : isGrant ? 'Grant' : 'Revoke'}
            className="flex-1"
            loading={loading}
            disabled={!selectedPermission}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </Modal>
  )
}

export default BulkPermissionsModal
